import React, { useEffect, useState, useMemo, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { CheckCircleIcon, XCircleIcon, SparklesIcon } from '@heroicons/react/24/solid';
import { ArrowPathIcon, CommandLineIcon, BriefcaseIcon, QueueListIcon, ShieldCheckIcon, WindowIcon } from '@heroicons/react/24/outline';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/Card';
import { Button } from '../components/ui/Button';
import { projectService } from '../services/projectService';
import { RootState } from '../store';

interface Step {
    id: string;
    label: string;
    status: 'pending' | 'loading' | 'success' | 'error';
    message?: string;
    type?: string;
}

export const ProjectGeneratingPage: React.FC = () => {
    const location = useLocation();
    const navigate = useNavigate();
    const { token } = useSelector((state: RootState) => state.auth);

    const { project, formData } = location.state || {};

    // Memoize the available steps based on preferences
    const initialSteps = useMemo(() => {
        if (!formData?.preferences) return [];
        return [
            { id: 'research', label: 'Project Research', status: 'success' as const, message: 'Context analyzed successfully' },
            { id: 'brd', label: 'Business Requirements (BRD)', status: 'pending' as const, type: 'BRD' },
            { id: 'prd', label: 'Product Requirements (PRD)', status: 'pending' as const, type: 'PRD' },
            { id: 'uir', label: 'UI Requirements (UIR)', status: 'pending' as const, type: 'UIR' },
            { id: 'nfr', label: 'Non-Functional Requirements (NFR)', status: 'pending' as const, type: 'NFR' },
        ].filter(step => {
            if (step.id === 'research') return true;
            return formData.preferences[step.id]?.enabled;
        });
    }, [formData]);

    const [steps, setSteps] = useState<Step[]>([]);
    const [isComplete, setIsComplete] = useState(false);

    useEffect(() => {
        if (initialSteps.length > 0 && steps.length === 0) {
            setSteps(initialSteps);
        }
    }, [initialSteps, steps]);

    const hasStarted = useRef(false);

    useEffect(() => {
        if (!project || !formData || steps.length === 0 || isComplete || hasStarted.current) {
            return;
        }

        const generate = async () => {
            if (hasStarted.current) return;
            hasStarted.current = true;
            console.log("Starting generation process...");

            const updateStep = (id: string, status: Step['status'], message?: string) => {
                setSteps(prev => prev.map(s => s.id === id ? { ...s, status, message: message || s.message } : s));
            };

            const itemsToGenerate = steps.filter(s => s.id !== 'research');

            // Generate requirements in parallel to speed up the process
            try {
                await Promise.all(itemsToGenerate.map(async (item, index) => {
                    // Small stagger to avoid overwhelming the server/AI in the exact same millisecond
                    await new Promise(resolve => setTimeout(resolve, index * 800));

                    updateStep(item.id, 'loading');

                    try {
                        const response = await projectService.generateRequirementBatch(project.id, {
                            type: item.type!,
                            count: formData.preferences[item.id]?.value || 5,
                            name: project.name,
                            description: formData.description,
                            technical: formData.technicalDetails,
                        });

                        if (response.success) {
                            updateStep(item.id, 'success', `${item.type} generated successfully`);
                        } else {
                            throw new Error(response.message || 'Failed to generate');
                        }
                    } catch (error: any) {
                        console.error(`Error generating ${item.type}:`, error);
                        const errorMessage = error.response?.data?.message || error.message || `Failed to generate ${item.type}`;
                        updateStep(item.id, 'error', errorMessage);
                    }
                }));
            } catch (globalError) {
                console.error("Global generation error:", globalError);
            } finally {
                setIsComplete(true);
            }
        };

        generate();
    }, [project, formData, token, steps.length, isComplete]);

    const getStepIcon = (id: string) => {
        switch (id) {
            case 'research': return <SparklesIcon className="h-5 w-5" />;
            case 'brd': return <BriefcaseIcon className="h-5 w-5" />;
            case 'prd': return <QueueListIcon className="h-5 w-5" />;
            case 'uir': return <WindowIcon className="h-5 w-5" />;
            case 'nfr': return <ShieldCheckIcon className="h-5 w-5" />;
            default: return <CommandLineIcon className="h-5 w-5" />;
        }
    };

    if (!project) {
        return <div className="p-10">Invalid project state. Please create a project from the dashboard.</div>
    }

    return (
        <div className="container mx-auto p-8 max-w-6xl space-y-8 min-h-screen bg-[#FDFCFB]">
            <div className="flex flex-col items-center text-center mb-10">
                <div className="mb-6 animate-pulse flex items-center justify-center gap-4">
                    <img src="/DocSprintLogo.png" alt="DocSprintAI" className="h-48 w-auto" />
                </div>
                <h1 className="text-4xl font-extrabold text-gray-900 mb-3 tracking-tight">
                    {isComplete ? 'Generation Complete!' : 'Crafting Your Vision'}
                </h1>
                <p className="text-lg text-gray-500 max-w-2xl">
                    Our AI agents are analyzing your requirements to build a comprehensive project structure.
                </p>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-3 gap-10">
                <div className="lg:col-span-1">
                    <Card className="border-orange-100 shadow-xl shadow-orange-500/5 rounded-3xl overflow-hidden sticky top-8">
                        <CardHeader className="bg-gradient-to-br from-orange-500/5 to-white border-b border-orange-50 p-6">
                            <CardTitle className="text-sm font-bold uppercase tracking-widest text-orange-600 flex items-center gap-2">
                                <ArrowPathIcon className={`h-4 w-4 ${!isComplete ? 'animate-spin' : ''}`} />
                                Process Status
                            </CardTitle>
                        </CardHeader>
                        <CardContent className="p-6">
                            <div className="space-y-4">
                                {steps.map(step => (
                                    <div
                                        key={step.id}
                                        className={`flex items-center gap-4 p-4 rounded-2xl transition-all duration-300 ${step.status === 'loading'
                                            ? 'bg-orange-50 border border-orange-100 shadow-sm scale-[1.02]'
                                            : 'bg-white border border-gray-50'
                                            }`}
                                    >
                                        <div className={`p-2.5 rounded-xl transition-colors ${step.status === 'success' ? 'bg-green-50 text-green-600' :
                                            step.status === 'error' ? 'bg-red-50 text-red-600' :
                                                step.status === 'loading' ? 'bg-orange-500 text-white animate-pulse shadow-lg shadow-orange-500/20' :
                                                    'bg-gray-50 text-gray-400'
                                            }`}>
                                            {step.status === 'success' ? <CheckCircleIcon className="h-5 w-5" /> :
                                                step.status === 'error' ? <XCircleIcon className="h-5 w-5" /> :
                                                    getStepIcon(step.id)}
                                        </div>
                                        <div className="flex-1 min-w-0">
                                            <p className={`text-sm font-bold truncate ${step.status === 'loading' ? 'text-orange-600' : 'text-gray-700'
                                                }`}>
                                                {step.label}
                                            </p>
                                            <p className="text-[11px] text-gray-400 font-medium truncate italic">
                                                {step.status === 'loading' ? 'Processing...' : (step.message || 'Waiting...')}
                                            </p>
                                        </div>
                                        {step.status === 'loading' && (
                                            <div className="flex gap-1">
                                                <div className="w-1 h-1 bg-orange-500 rounded-full animate-bounce" style={{ animationDelay: '0s' }}></div>
                                                <div className="w-1 h-1 bg-orange-500 rounded-full animate-bounce" style={{ animationDelay: '0.2s' }}></div>
                                                <div className="w-1 h-1 bg-orange-500 rounded-full animate-bounce" style={{ animationDelay: '0.4s' }}></div>
                                            </div>
                                        )}
                                    </div>
                                ))}
                            </div>
                        </CardContent>
                    </Card>
                </div>

                <div className="lg:col-span-2 space-y-6">
                    <Card className="border-gray-100 shadow-sm rounded-3xl overflow-hidden bg-white">
                        <CardHeader className="p-8 border-b border-gray-50">
                            <div className="flex items-center justify-between">
                                <h3 className="text-xl font-bold text-gray-900">Project Overview</h3>
                                <span className={`px-4 py-1 rounded-full text-xs font-bold uppercase tracking-widest ${isComplete ? 'bg-green-100 text-green-700' : 'bg-orange-100 text-orange-700 animate-pulse'
                                    }`}>
                                    {isComplete ? 'Ready' : 'Generating'}
                                </span>
                            </div>
                        </CardHeader>
                        <CardContent className="p-8 space-y-8">
                            <div className="grid grid-cols-1 md:grid-cols-[140px_1fr] gap-6">
                                <span className="text-xs font-bold text-gray-400 uppercase tracking-widest pt-1">Project Name</span>
                                <span className="text-lg font-bold text-gray-900">{project.name}</span>
                            </div>

                            <div className="grid grid-cols-1 md:grid-cols-[140px_1fr] gap-6">
                                <span className="text-xs font-bold text-gray-400 uppercase tracking-widest pt-1">Description</span>
                                <span className="text-gray-600 text-sm leading-relaxed whitespace-pre-line">{formData.description}</span>
                            </div>

                            <div className="grid grid-cols-1 md:grid-cols-[140px_1fr] gap-6">
                                <span className="text-xs font-bold text-gray-400 uppercase tracking-widest pt-1">Architecture</span>
                                <div className="bg-gray-50 p-6 rounded-2xl border border-gray-100 font-mono text-[13px] text-gray-700 leading-relaxed">
                                    {formData.technicalDetails}
                                </div>
                            </div>

                            {!isComplete && (
                                <div className="mt-8 pt-8 border-t border-gray-50">
                                    <div className="flex items-center gap-3 text-orange-600 bg-orange-50/50 p-4 rounded-2xl border border-orange-100/50">
                                        <SparklesIcon className="h-5 w-5 animate-pulse" />
                                        <p className="text-xs font-medium italic">
                                            DocSprintAI is currently outlining the core architecture and defining requirement boundaries...
                                        </p>
                                    </div>
                                </div>
                            )}
                        </CardContent>
                    </Card>
                </div>
            </div>

            {isComplete && (
                <div className="fixed bottom-10 left-1/2 -translate-x-1/2 z-50 animate-in slide-in-from-bottom-10 duration-500">
                    <div className="bg-white border border-green-100 text-gray-900 px-8 py-5 rounded-3xl shadow-[0_20px_50px_rgba(0,0,0,0.1)] flex items-center gap-8 backdrop-blur-xl bg-white/90">
                        <div className="flex items-center gap-4 border-r border-gray-100 pr-8">
                            <div className="bg-green-100 p-3 rounded-2xl">
                                <CheckCircleIcon className="h-6 w-6 text-green-600" />
                            </div>
                            <div>
                                <div className="font-extrabold text-gray-900">Project Ready!</div>
                                <div className="text-xs text-gray-500 font-medium tracking-wide">{project.name} set up complete.</div>
                            </div>
                        </div>
                        <Button
                            size="lg"
                            className="bg-gradient-to-r from-orange-500 to-orange-600 hover:from-orange-600 hover:to-orange-700 text-white border-none shadow-lg shadow-orange-500/20 rounded-2xl px-8 font-bold transition-all hover:scale-105 active:scale-95"
                            onClick={() => navigate(`/projects/${project.id}`)}
                        >
                            Explore Project
                        </Button>
                    </div>
                </div>
            )}
        </div>
    );
};
